import { ModalValHint } from "../../modals/ModalValHint";
import { CADENCE_QUESTION } from "../../../lib/questionCopy";

/**
 * "How often should a box arrive?" (wizard step "cadence", D-16) — the drop-cadence
 * decision on its own screen (moved out of the old Fuel step's cadenceSlot).
 * Cadence is event/journey-scoped, so the state stays with the orchestrator.
 *
 * D4: "Every 2 weeks · Most popular" arrives preselected, so Continue never
 * waits on a pick.
 *
 * Variants: the race path shows the standard cadence cards; the no-race path
 * (a rolling journey) adds its own desc + the "How far ahead should we plan?"
 * coverage chips.
 */
export function BoxCadenceStep({
  dropWeeks,
  setDropWeeks,
  rolling,
  coverageWeeks,
  setCoverageWeeks,
  coverageChoices,
  nudge
}: {
  dropWeeks: number;
  setDropWeeks: (n: number) => void;
  /** The no-race path, which finishes by creating a rolling journey. */
  rolling: boolean;
  /** No-race (rolling) path only — initial coverage (weeks forward to tile). */
  coverageWeeks: number;
  setCoverageWeeks: (n: number) => void;
  coverageChoices: readonly number[];
  nudge: string | null;
}) {
  return (
    <div className="fgp-wizard-panel">
      <div className="fgp-wz-field fgp-wz-cadence">
        {rolling ? (
          <p className="fgp-wz-fuel__desc">
            No race in mind — we roll fuel boxes forward to match whatever you plan. We
            prepare each box when you&rsquo;re ready for it — you review and buy them one at
            a time. Nothing upfront.
          </p>
        ) : (
          <p className="fgp-wz-fuel__desc">{CADENCE_QUESTION.desc}</p>
        )}
        <div className="fgp-wizard-grid-4 fgp-wizard-grid-4--mobile">
          {[1, 2, 3, 4].map((n) => {
            const on = dropWeeks === n;
            const sub = n === 2 ? "Most popular" : null;
            return (
              <button
                key={n}
                type="button"
                aria-pressed={on}
                onClick={() => setDropWeeks(n)}
                className={`fgp-modal-choice-card${on ? " fgp-modal-choice-card--on" : ""}`}>
                <div className="fgp-modal-choice-card__title">
                  Every {n === 1 ? "week" : `${n} weeks`}
                </div>
                {sub != null ? (
                  <div
                    className="fgp-modal-choice-card__sub"
                    style={n === 2 ? { color: "var(--fg-purple-deep)", fontWeight: 700 } : undefined}>
                    {sub}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
        {rolling && (
          <>
            <div className="fgp-wz-fuel__label" style={{ marginTop: 14 }}>
              How far ahead should we plan?
            </div>
            <div className="fgp-window-chiprow" role="radiogroup" aria-label="Initial coverage">
              {coverageChoices.map((w) => {
                const on = coverageWeeks === w;
                return (
                  <button
                    key={w}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`fg-chip${on ? " is-on" : ""}`}
                    onClick={() => setCoverageWeeks(w)}>
                    {w} weeks
                  </button>
                );
              })}
            </div>
          </>
        )}
        {/* Cadence only — no box count (rolling boxes open on workouts). */}
        <p className="fgp-window-caption">
          A new box every {dropWeeks === 1 ? "week" : `${dropWeeks} weeks`}.
        </p>
      </div>
      <ModalValHint message={nudge} />
    </div>
  );
}
